import { documentCookie, withXsrfHeader } from './xsrf-header';

/**
 * A dashboard card's direct write: the frame posts `{kind:'act'}`, and the HOST asks the harness to prepare
 * it, draws the confirm itself and executes it. The harness's public `HarnessService` takes these calls only
 * from the metasite owner's own session, so they go from the browser, same origin, with the user's cookie —
 * a gateway route like the dashboard render, not the app mount (`API_BASE`), and never through a server route
 * that would carry another identity.
 *
 * No runtime imports beyond `xsrf-header`: tests drive it directly (orion-web/CLAUDE.md, "a unit test must
 * not import the app's dependency graph").
 */
const ACTIONS_BASE = '/_api/symphony-harness/v1';

export interface CardActionRequest {
  dashboardSlug: string;
  widgetSlug: string;
  /** The drill-in page a button outside every card slot sits on (`<widget>-detail`); `widgetSlug` is then empty. The
   *  harness resolves the action from that page's own published pin and page manifest. Left out for a card's act. */
  pageSlug?: string;
  op: string;
  record: string;
  /** The component version the card was rendered from. Prepare refuses with CARD_CHANGED unless it is current. */
  componentVersion: number;
  /** What the owner typed into the card's form for an op that asks for input (`checkedArgs`); `{}` on a first press. */
  args: Record<string, string>;
}

/** One field of the form an op that asks for input sends with INPUT_REQUIRED. The host passes it into the frame,
 *  which draws it in the row that pressed. */
export interface CardActionInputField {
  key: string;
  label: string;
  /** TEXT or CHOICE; UNKNOWN_KIND when the JSON left it out. */
  kind: string;
  required: boolean;
  maxLength: number;
  options: { value: string; label: string }[];
  hint: string;
  value: string;
  error: string;
}

/** PrepareCardAction's and UndoPrepareCardAction's answer. `token` is a credential: it never enters the frame. */
export interface PreparedCardAction {
  title: string;
  lines: string[];
  destructive: boolean;
  customerNotice: string;
  /** The op can skip what Wix tells the customer (a cancellation's email): the dialog offers a switch for it,
   *  on by default. False for every op that cannot, and the JSON leaves it out then. */
  offersCustomerNotification: boolean;
  reversible: boolean;
  token: string;
  refused: boolean;
  reason: string;
  fallbackPrompt: string;
  /** Set with INPUT_REQUIRED: the form the owner fills in before the op can be prepared. */
  inputFields: CardActionInputField[];
  /** What the owner should do about a refusal, in English; empty when there is nothing. */
  notice: string;
}

export interface ExecutedCardAction {
  ok: boolean;
  undoToken: string;
  refused: boolean;
  reason: string;
  /** A new booking's id (`bookings.create`); empty for every other op. */
  createdId: string;
}

/** The Calendar booking card's new appointment, as PrepareCalendarAction's typed `create` takes it. */
export interface CalendarBookingCreate {
  serviceId: string;
  /** RFC 3339. */
  start: string;
  staffResourceId: string;
  contactId?: string;
  clientFirstName?: string;
  clientLastName?: string;
  clientEmail?: string;
  clientPhone?: string;
}

/** Not a harness code: the call was turned away or failed before the harness answered, so nothing ran. */
export const UNAVAILABLE = 'UNAVAILABLE';
/** The harness's code for a write Wix never answered, which may have happened; also used when execute's own call fails mid-flight. */
export const OUTCOME_UNKNOWN = 'OUTCOME_UNKNOWN';

/** The frame's `record`, as the request carries it: the bridge sends a scalar, anything else is no record. */
export const actRecord = (record: unknown): string =>
  typeof record === 'string' || typeof record === 'number' || typeof record === 'boolean' ? String(record) : '';

/** The frame's `version`: an integer, else 0, which prepare refuses as a changed card. */
export const actVersion = (version: unknown): number => (Number.isInteger(version) ? (version as number) : 0);

const PAGE_SLUG = /^[a-z0-9][a-z0-9-]{0,149}$/;

/**
 * The frame's `page`: the drill-in page an act from outside every card slot names (runtime.js `directPage`), else ''.
 * Only a slug's shape travels; a card's act sends '' and its widget names it.
 */
export const actPage = (page: unknown): string => (typeof page === 'string' && PAGE_SLUG.test(page) ? page : '');

/**
 * PrepareCardAction's request for one act: a card's names its widget, and a page's act (no widget) names the page.
 * Mirrored by iOS `CardActions.prepareBody` and Android `CardActions.prepareBody`.
 */
export function cardActionRequest(
  dashboardSlug: string,
  act: { widget: string; page?: string; op: string; record: string; version: number },
  args: Record<string, string>,
): CardActionRequest {
  const page = act.widget ? '' : actPage(act.page);
  return {
    dashboardSlug,
    widgetSlug: act.widget,
    ...(page ? { pageSlug: page } : {}),
    op: act.op,
    record: act.record,
    componentVersion: act.version,
    args,
  };
}

/** The harness's code for a token that is gone: expired, consumed, or never issued. An undo token is otherwise
 *  still good after a refused undo-prepare, so only this one ends the Undo offer. */
export const TOKEN_INVALID = 'TOKEN_INVALID';

/** Not a harness code: the host refused the act itself, because another write or a widget confirm is open, or its
 *  dashboard is hidden. */
export const BUSY = 'BUSY';
/** The harness's code for a malformed request; the host uses it for an act it refuses before any call. */
export const INVALID_REQUEST = 'INVALID_REQUEST';
/** The harness's code for an op that needs the owner's input first: the frame draws `inputFields` as a form. */
export const INPUT_REQUIRED = 'INPUT_REQUIRED';

/** What the host shows for a prepare refusal that describes the record itself. */
export interface RefusalNotice {
  messageKey: string;
  /** The notice offers "Ask in chat", which sends the fallback prompt. */
  asksInChat: boolean;
}

/**
 * Prepare refusals that are about the record, where opening chat by itself would not make the one-tap
 * action possible. The app shows a notice instead; those where the agent can still help offer "Ask in
 * chat", so the owner opts in. Every other refusal keeps the chat fallback, unless the harness sent a `notice` to
 * show instead. Mirrored exactly by iOS `CardActions.refusalNotice` and Android `CardActions.refusalNotice`.
 */
const REFUSAL_NOTICES: Record<string, RefusalNotice> = {
  MULTI_LINE_ORDER: { messageKey: 'cardAction.refused.multiLineOrder', asksInChat: true },
  COURSE_BOOKING: { messageKey: 'cardAction.refused.courseBooking', asksInChat: true },
  ALREADY_DONE: { messageKey: 'cardAction.refused.alreadyDone', asksInChat: false },
  RECENTLY_DONE: { messageKey: 'cardAction.refused.alreadyDone', asksInChat: false },
  IN_FLIGHT: { messageKey: 'cardAction.refused.inProgress', asksInChat: false },
  TOO_EARLY: { messageKey: 'cardAction.refused.tooEarly', asksInChat: false },
  NO_ORDER: { messageKey: 'cardAction.refused.noOrder', asksInChat: true },
  PAYMENT_DECISION_REQUIRED: { messageKey: 'cardAction.refused.notNow', asksInChat: true },
  PRECONDITION_FAILED: { messageKey: 'cardAction.refused.notNow', asksInChat: true },
  REFUND_FIRST: { messageKey: 'cardAction.refused.refundFirst', asksInChat: true },
  REFUND_IN_WIX: { messageKey: 'cardAction.refused.refundInWix', asksInChat: true },
  OTHER_APP_LINE: { messageKey: 'cardAction.refused.otherAppLine', asksInChat: false },
};

export const refusalNotice = (reason: string): RefusalNotice | undefined =>
  Object.prototype.hasOwnProperty.call(REFUSAL_NOTICES, reason) ? REFUSAL_NOTICES[reason] : undefined;

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EVENT_ORDER_NUMBER = /^[A-Z0-9]{2,12}(-[A-Z0-9]{2,12}){1,4}$/;
const MAX_EVENT_ORDER_NUMBER_LENGTH = 36;
const MAX_OP_LENGTH = 100;

const isEventOrderNumber = (record: string): boolean =>
  record.length <= MAX_EVENT_ORDER_NUMBER_LENGTH && EVENT_ORDER_NUMBER.test(record);

/**
 * What the host keeps of a frame's act before anything leaves it. Every record a card acts on is a GUID (a
 * booking or an order id) or an event order number, so any other record is refused here, without a call, and is
 * never logged: a card script can put anything in it, an email or a phone number among them. `op` is cut to the
 * harness's limit for the same reason; a longer one is refused.
 */
export function checkedAct(op: string, record: string): { op: string; record: string; valid: boolean } {
  const recordOk = GUID.test(record) || isEventOrderNumber(record);
  return {
    op: op.slice(0, MAX_OP_LENGTH),
    record: recordOk ? record : '',
    valid: recordOk && op.length > 0 && op.length <= MAX_OP_LENGTH,
  };
}

const MAX_ARGS = 10;
const MAX_ARG_KEY_LENGTH = 40;
const MAX_ARG_VALUE_LENGTH = 500;

/**
 * The frame's `args`, the owner's answers to an op's form, kept only within the harness's limits: at most 10 string
 * values, each keyed by a non-empty key. Anything else is null, which the host refuses without a call, as the harness
 * would. An older card sends none, which is `{}`. The values are what the owner typed, so they are never logged.
 */
export function checkedArgs(args: unknown): Record<string, string> | null {
  if (args === undefined) return {};
  if (Object.prototype.toString.call(args) !== '[object Object]') return null;
  const entries = Object.entries(args as Record<string, unknown>);
  if (entries.length > MAX_ARGS) return null;
  const checked: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (!key || key.length > MAX_ARG_KEY_LENGTH || typeof value !== 'string' || value.length > MAX_ARG_VALUE_LENGTH) return null;
    checked[key] = value;
  }
  return checked;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const toInputField = (raw: Record<string, unknown>): CardActionInputField => ({
  key: text(raw.key),
  label: text(raw.label),
  kind: text(raw.kind) || 'UNKNOWN_KIND',
  required: raw.required === true,
  maxLength: typeof raw.maxLength === 'number' ? raw.maxLength : 0,
  options: Array.isArray(raw.options) ? raw.options.filter(isObject).map((o) => ({ value: text(o.value), label: text(o.label) })) : [],
  hint: text(raw.hint),
  value: text(raw.value),
  error: text(raw.error),
});

// The proto's JSON leaves out every field at its default, so a missing one is its zero value.
const toPrepared = (raw: Record<string, unknown>): PreparedCardAction => ({
  title: text(raw.title),
  lines: Array.isArray(raw.lines) ? raw.lines.filter((line): line is string => typeof line === 'string') : [],
  destructive: raw.destructive === true,
  customerNotice: text(raw.customerNotice),
  offersCustomerNotification: raw.offersCustomerNotification === true,
  reversible: raw.reversible === true,
  token: text(raw.token),
  refused: raw.refused === true || !text(raw.token),
  reason: text(raw.reason),
  fallbackPrompt: text(raw.fallbackPrompt),
  inputFields: Array.isArray(raw.inputFields) ? raw.inputFields.filter(isObject).map(toInputField) : [],
  notice: text(raw.notice),
});

const refusedPrepare = (reason: string): PreparedCardAction => ({
  title: '',
  lines: [],
  destructive: false,
  customerNotice: '',
  offersCustomerNotification: false,
  reversible: false,
  token: '',
  refused: true,
  reason,
  fallbackPrompt: '',
  inputFields: [],
  notice: '',
});

type Posted = { ok: true; body: Record<string, unknown> } | { ok: false; status: number };

async function post(method: string, body: object): Promise<Posted> {
  try {
    const res = await fetch(
      `${ACTIONS_BASE}/${method}`,
      withXsrfHeader(
        { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
        documentCookie(),
      ),
    );
    if (!res.ok) return { ok: false, status: res.status };
    const json: unknown = await res.json();
    return { ok: true, body: json && typeof json === 'object' ? (json as Record<string, unknown>) : {} };
  } catch {
    return { ok: false, status: 0 };
  }
}

export async function prepareCardAction(request: CardActionRequest): Promise<PreparedCardAction> {
  const res = await post('card-actions/prepare', request);
  return res.ok ? toPrepared(res.body) : refusedPrepare(UNAVAILABLE);
}

interface CalendarActionBase {
  siteId: string;
  record: string;
}

/** A change to one booking the Calendar's details card makes; the harness re-checks it against the live booking. */
export type CalendarActionRequest =
  | (CalendarActionBase & {
      op: 'bookings.approve' | 'bookings.cancel' | 'bookings.checkIn' | 'bookings.decline' | 'bookings.markNoShow' | 'bookings.markPaid';
    })
  | (CalendarActionBase & { op: 'bookings.reschedule'; reschedule: { start: string } });

/** The Calendar's own prepare: the owner's site, a booking op, and its record (a new booking's is its request id). */
export async function prepareCalendarAction(
  request: CalendarActionRequest | (CalendarActionBase & { op: 'bookings.create'; create: CalendarBookingCreate }),
): Promise<PreparedCardAction> {
  const res = await post('calendar-actions/prepare', request);
  return res.ok ? toPrepared(res.body) : refusedPrepare(UNAVAILABLE);
}

export async function undoPrepareCardAction(undoToken: string): Promise<PreparedCardAction> {
  const res = await post('card-actions/undo-prepare', { undoToken });
  return res.ok ? toPrepared(res.body) : refusedPrepare(UNAVAILABLE);
}

/**
 * `skipCustomerNotification` is the owner turning off the dialog's "Notify the customer" switch, which is only
 * offered when prepare said `offersCustomerNotification`. It is sent only when true: the harness reads a missing
 * field as "notify", so every op without the switch sends exactly what it did before.
 */
export async function executeCardAction(
  token: string,
  {
    skipCustomerNotification = false,
    note,
    calendarConversationId,
  }: {
    skipCustomerNotification?: boolean;
    /** The owner's message to the customer, sent inside Wix's notification of an op that offers one while it is on. */
    note?: string;
    /** For the booking card's Book: the Calendar chat it was in, where the harness notes the new booking without a turn. */
    calendarConversationId?: string;
  } = {},
): Promise<ExecutedCardAction> {
  const res = await post('card-actions/execute', {
    token,
    ...(skipCustomerNotification ? { skipCustomerNotification: true } : {}),
    ...(note ? { note } : {}),
    ...(calendarConversationId ? { calendarConversationId } : {}),
  });
  if (!res.ok) {
    // A 4xx is the gateway or the harness turning the call away before it ran; anything else may have
    // reached Wix, and saying "nothing changed" about a payment that went through is the worse error.
    const refusedUpfront = res.status >= 400 && res.status < 500;
    return { ok: false, undoToken: '', refused: true, reason: refusedUpfront ? UNAVAILABLE : OUTCOME_UNKNOWN, createdId: '' };
  }
  const ok = res.body.ok === true;
  return {
    ok,
    undoToken: ok ? text(res.body.undoToken) : '',
    refused: !ok,
    reason: ok ? '' : text(res.body.reason) || OUTCOME_UNKNOWN,
    createdId: ok ? text(res.body.createdId) : '',
  };
}

export type CardActionStage = 'shown' | 'confirmed' | 'cancelled' | 'refused' | 'done' | 'undone';

export interface CardActionBiEvent {
  type: 'itemSeen' | 'clickCta';
  componentName: string;
  additionalFields: {
    action: string;
    outcome: 'do' | 'undo' | 'success' | 'failure';
    durationBucket?: CardActionDurationBucket;
  };
}

export type CardActionDurationBucket = 'under-1s' | '1-3s' | '3-10s' | '10s-plus';

export function cardActionDurationBucket(durationMs: number): CardActionDurationBucket {
  if (durationMs < 1_000) return 'under-1s';
  if (durationMs < 3_000) return '1-3s';
  if (durationMs < 10_000) return '3-10s';
  return '10s-plus';
}

const CALENDAR_ACTIONS = new Set([
  'bookings.approve',
  'bookings.cancel',
  'bookings.checkIn',
  'bookings.decline',
  'bookings.markNoShow',
  'bookings.markPaid',
  'bookings.reschedule',
  'bookings.changeStaff',
]);

function cardActionKind(op: string): string {
  return CALENDAR_ACTIONS.has(op) ? `booking-${op.slice('bookings.'.length)}` : 'other';
}

function cardActionOutcome(stage: CardActionStage, outcome: string): CardActionBiEvent['additionalFields']['outcome'] {
  if (stage === 'done' || stage === 'undone') return 'success';
  if (stage === 'refused') return 'failure';
  return outcome === 'undo' ? 'undo' : 'do';
}

export function cardActionBiEvent(stage: CardActionStage, op: string, outcome: string, durationMs?: number): CardActionBiEvent {
  return {
    type: stage === 'shown' ? 'itemSeen' : 'clickCta',
    componentName: `card-action-${stage}`,
    additionalFields: {
      action: cardActionKind(op),
      outcome: cardActionOutcome(stage, outcome),
      ...(durationMs === undefined ? {} : { durationBucket: cardActionDurationBucket(durationMs) }),
    },
  };
}
